/**
 * Scanner-Schutz für Ansichten ohne Verkauf (Einrichtung, Ersteinrichtung/Geräteaktivierung,
 * Handterminal-Einrichtung): Dort läuft keine Scan-Erkennung des Verkaufs, ein Handscanner
 * (USB-HID bzw. Tastatur-Wedge) tippt sonst seinen Code samt Enter in das fokussierte Feld bzw.
 * löst mit Enter die fokussierte Taste aus (z. B. „Abbrechen“ der Aktivierung).
 *
 * - **Gesperrte Felder** (`data-scansperre`, Passwort-, PIN- und Einmalcodefelder – siehe
 *   {@link feldScanSperre}): Eine schnelle Zeichenfolge mit Enter/Tab wird verworfen, das Feld
 *   steht danach wieder auf dem Wert vor der Folge, das Abschlusszeichen wirkt nicht (kein
 *   Absenden), und der Aufrufer zeigt den Hinweis. Kurze schnelle Folgen (1–3 Zeichen mit
 *   sofortigem Enter) leeren das Feld wie „Gegeben“ im Bar-Dialog.
 * - **Ohne Feld** (Fokus auf einer Taste bzw. der Seite): Das Enter eines Scans löst nichts aus.
 * - Normales Tippen (Touch, Bildschirmtastatur, Hardware-Tastatur) bleibt unverändert; nicht
 *   markierte Felder sind nicht betroffen. Felder mit eigener Scanerkennung
 *   (`data-eigene-scanerkennung`, PIN der Bedieneranmeldung) lässt der Schutz aus.
 *
 * Dieselbe Felderkennung wie im Verkauf ({@link FeldScanErkennung}, auch Scannerpräfix und
 * Android-WebView mit `beforeinput`). Nicht zusammen mit dem Verkauf einhängen – dort
 * übernimmt dessen eigener Feldpfad.
 */
import { useEffect, useRef } from 'react';

import { gesperrterScan, feldScanSperre } from './scan-verkauf';
import {
  feldEingabeDaten,
  FeldScanErkennung,
  istNachlauf,
  ladeScannerPraefix,
  PRAEFIX_ABSTAND_MS,
  type FeldVerworfen,
} from './scanner';

type Feld = HTMLInputElement | HTMLTextAreaElement;

/** Taste, wie der Schutz sie braucht (keydown bzw. aus beforeinput nachgebildet). */
interface Taste {
  key: string;
  timeStamp: number;
  preventDefault(): void;
  stopPropagation(): void;
}

/** Zeitpunkt der Taste: Zeitstempel des Ereignisses, sonst jetzt (wie im Verkauf). */
function tastenZeit(zeitstempel: number): number {
  const jetzt = performance.now();
  return zeitstempel > 0 && zeitstempel <= jetzt + 1 ? zeitstempel : jetzt;
}

/** Feldinhalt setzen, sodass React die Änderung sieht (nativer Setter + input-Ereignis). */
function setzeFeldwert(feld: Feld, wert: string): void {
  const prototyp =
    feld instanceof HTMLInputElement ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
  Object.getOwnPropertyDescriptor(prototyp, 'value')?.set?.call(feld, wert);
  feld.dispatchEvent(new Event('input', { bubbles: true }));
}

function praefix(): string | null {
  try {
    return ladeScannerPraefix(localStorage);
  } catch {
    return null;
  }
}

function istFeld(ziel: EventTarget | null): ziel is Feld {
  return ziel instanceof HTMLInputElement || ziel instanceof HTMLTextAreaElement;
}

/** Meldung zu einem verworfenen Scan ohne Feld (Enter auf einer Taste). */
export function scanOhneFeldHinweis(code: string): string {
  return `Scan ${code} ignoriert – hier werden keine Scans übernommen`;
}

/**
 * Schutz in `fenster` einhängen (Capture-Phase, vor den Feldern). `onVerworfen` erhält die
 * Meldung zu jedem verworfenen Scan. Rückgabe: Aushängen.
 */
export function installiereScanSchutz(
  fenster: Window,
  onVerworfen: (meldung: string) => void,
): () => void {
  const feldScan = new FeldScanErkennung();
  const seitenScan = new FeldScanErkennung();
  let letzterScan = Number.NEGATIVE_INFINITY;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const verwerfen = (t: Taste): void => {
    t.preventDefault();
    t.stopPropagation();
  };
  const praefixVerworfen = (feld: Feld, v: FeldVerworfen): void => {
    letzterScan = performance.now();
    setzeFeldwert(feld, v.wertVorher);
    onVerworfen(
      `Scan${v.code === '' ? '' : ` ${v.code}`} unvollständig – nichts übernommen, bitte erneut scannen`,
    );
  };
  const praefixUeberwachen = (feld: Feld): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (!feldScan.praefixLaeuft()) {
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      const v = feldScan.ablauf(performance.now());
      if (v !== null) {
        praefixVerworfen(feld, v);
      } else {
        praefixUeberwachen(feld);
      }
    }, PRAEFIX_ABSTAND_MS + 20);
  };

  /** Taste in einem gesperrten Feld (wie der Zweig „gesperrt“ im Feldpfad des Verkaufs). */
  const feldTaste = (t: Taste, feld: Feld, sperre: string): void => {
    const jetzt = tastenZeit(t.timeStamp);
    if (!feldScan.hatZeichen(jetzt) && istNachlauf(t.key, jetzt, letzterScan)) {
      verwerfen(t);
      return;
    }
    const treffer = feldScan.taste(t.key, jetzt, feld.value, praefix());
    praefixUeberwachen(feld);
    if (treffer === null) {
      return;
    }
    if (treffer === 'praefix' || treffer === 'schlucken' || treffer === 'nachlauf') {
      verwerfen(t);
      return;
    }
    if ('restVerworfen' in treffer) {
      verwerfen(t);
      letzterScan = jetzt;
      onVerworfen(
        `Scan${treffer.restVerworfen === '' ? '' : ` ${treffer.restVerworfen}`} unvollständig – nichts übernommen, bitte erneut scannen`,
      );
      return;
    }
    if ('verworfen' in treffer) {
      verwerfen(t);
      praefixVerworfen(feld, treffer);
      return;
    }
    verwerfen(t);
    letzterScan = jetzt;
    if (treffer.kurz) {
      // Scan und getippte Zeichen lassen sich nicht sicher trennen – Feld leeren
      setzeFeldwert(feld, '');
      onVerworfen(
        `Eingabe ${treffer.code} verworfen (${sperre}) – möglicher Scan; bitte neu eingeben`,
      );
      return;
    }
    setzeFeldwert(feld, treffer.wertVorher);
    onVerworfen(gesperrterScan(treffer.code, sperre));
  };

  /** Taste ohne Feld: nur das Abschlusszeichen eines Scans wird verworfen. */
  const seitenTaste = (t: Taste): void => {
    const jetzt = tastenZeit(t.timeStamp);
    if (!seitenScan.hatZeichen(jetzt) && istNachlauf(t.key, jetzt, letzterScan)) {
      verwerfen(t);
      return;
    }
    const treffer = seitenScan.taste(t.key, jetzt, '', praefix());
    if (treffer === null) {
      return;
    }
    // Ohne Feld schreibt kein Zeichen etwas; Präfix, Rest und Abschluss wirken nie als Taste
    verwerfen(t);
    if (treffer === 'praefix' || treffer === 'schlucken' || treffer === 'nachlauf') {
      return;
    }
    letzterScan = jetzt;
    const code = 'restVerworfen' in treffer ? treffer.restVerworfen : treffer.code;
    onVerworfen(scanOhneFeldHinweis(code));
  };

  const taste = (e: KeyboardEvent): void => {
    if (
      e.isComposing ||
      e.altKey ||
      e.ctrlKey ||
      e.metaKey ||
      e.target instanceof HTMLSelectElement
    ) {
      return;
    }
    const ziel = e.target;
    if (istFeld(ziel)) {
      if (ziel.dataset.eigeneScanerkennung !== undefined) {
        return;
      }
      const sperre = feldScanSperre(ziel);
      if (sperre !== null) {
        feldTaste(e, ziel, sperre);
      }
      return;
    }
    if (ziel instanceof HTMLElement && ziel.isContentEditable) {
      return;
    }
    seitenTaste(e);
  };
  // Android-WebView: Zeichen ohne verwertbares keydown kommen nur als beforeinput (A4)
  const eingabe = (e: Event): void => {
    const ziel = e.target;
    if (
      !istFeld(ziel) ||
      ziel.dataset.eigeneScanerkennung !== undefined ||
      !(e instanceof InputEvent)
    ) {
      return;
    }
    const sperre = feldScanSperre(ziel);
    const daten = feldEingabeDaten(e);
    if (sperre === null || daten === null) {
      return;
    }
    for (const key of feldScan.eingabeZeichen(daten, tastenZeit(e.timeStamp))) {
      feldTaste(
        {
          key,
          timeStamp: e.timeStamp,
          preventDefault: () => {
            e.preventDefault();
          },
          stopPropagation: () => {
            e.stopPropagation();
          },
        },
        ziel,
        sperre,
      );
    }
    feldScan.eingabeGezaehlt();
  };

  fenster.addEventListener('keydown', taste, true);
  fenster.addEventListener('beforeinput', eingabe, true);
  return () => {
    fenster.removeEventListener('keydown', taste, true);
    fenster.removeEventListener('beforeinput', eingabe, true);
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
}

/** Hook: Scanner-Schutz, solange die Ansicht eingehängt ist (siehe {@link installiereScanSchutz}). */
export function useScanSchutz(onVerworfen: (meldung: string) => void): void {
  const melden = useRef(onVerworfen);
  melden.current = onVerworfen;
  useEffect(
    () =>
      installiereScanSchutz(window, (m) => {
        melden.current(m);
      }),
    [],
  );
}
